import { Container, Graphics, Text } from 'pixi.js';

// ─── Typography ───────────────────────────────────────────────

/** Drop shadow for all Gugi header/title text — gives an embossed, stamped look. */
export const GUGI_SHADOW = {
  alpha: 0.55,
  angle: Math.PI / 4,
  blur: 0,
  color: 0x000000,
  distance: 2,
} as const;

// ─── Color helpers ───────────────────────────────────────────

/** Lighten a hex color by `amount` (0–1 fraction of full brightness). */
export function lighten(hex: number, amount: number): number {
  const r = Math.min(255, ((hex >> 16) & 0xff) + Math.round(255 * amount));
  const g = Math.min(255, ((hex >> 8)  & 0xff) + Math.round(255 * amount));
  const b = Math.min(255, ( hex        & 0xff)  + Math.round(255 * amount));
  return (r << 16) | (g << 8) | b;
}

/** Darken a hex color by `amount` (0–1). */
export function darken(hex: number, amount: number): number {
  const r = Math.max(0, ((hex >> 16) & 0xff) - Math.round(255 * amount));
  const g = Math.max(0, ((hex >> 8)  & 0xff) - Math.round(255 * amount));
  const b = Math.max(0, ( hex        & 0xff)  - Math.round(255 * amount));
  return (r << 16) | (g << 8) | b;
}


// ─── Bevel panel ─────────────────────────────────────────────

export interface BevelPanelOptions {
  /** Base fill color.  Default 0x0c0e14 */
  fill?: number;
  /** Border/stroke color.  Default 0x1a2030 */
  border?: number;
  /** Corner radius.  Default 6 */
  radius?: number;
  /** Shadow-plate offset in px.  Default 3 */
  depth?: number;
  /** Draw corner rivet bolts.  Default false */
  rivets?: boolean;
  /** If provided, draws a 2 px accent line across the top edge. */
  accentEdge?: number;
  /** Face fill alpha (0–1).  Default 1. Useful for overlaid panels. */
  alpha?: number;
}

/**
 * Draws a pseudo-3D beveled panel onto `g`.
 *
 * Layer order (back → front):
 *   1. Shadow plate  — dark roundRect offset down+right
 *   2. Panel face    — vertical gradient fill (top lighter, bottom darker)
 *   3. Border stroke — subtle 1 px border
 *   4. Top highlight — bright 1 px rim at top edge
 *   5. Bottom shadow — dark 1 px rim at bottom edge
 *   6. Accent edge   — optional 2 px coloured top line
 *   7. Rivets        — optional corner bolts
 */
export function drawBevelPanel(
  g: Graphics,
  x: number, y: number, w: number, h: number,
  opts: BevelPanelOptions = {},
): void {
  const fill   = opts.fill   ?? 0x0c0e14;
  const border = opts.border ?? 0x1a2030;
  const r      = opts.radius ?? 6;
  const depth  = opts.depth  ?? 3;
  const alpha  = opts.alpha  ?? 1;

  // 1. Shadow plate
  g.roundRect(x + depth, y + depth, w, h, r);
  g.fill({ color: darken(fill, 0.06), alpha: alpha * 0.65 });

  // 2. Panel face with vertical gradient
  g.roundRect(x, y, w, h, r);
  g.fill({ color: fill, alpha });

  // 3. Border
  g.roundRect(x, y, w, h, r);
  g.stroke({ color: border, width: 1 });

  // 4. Top highlight rim
  g.moveTo(x + r,     y + 0.5);
  g.lineTo(x + w - r, y + 0.5);
  g.stroke({ color: lighten(fill, 0.2), width: 1, alpha: 0.5 });

  // 5. Bottom shadow rim
  g.moveTo(x + r,     y + h - 0.5);
  g.lineTo(x + w - r, y + h - 0.5);
  g.stroke({ color: darken(fill, 0.15), width: 1, alpha: 0.55 });

  // 6. Accent top edge
  if (opts.accentEdge !== undefined) {
    g.moveTo(x + r,     y);
    g.lineTo(x + w - r, y);
    g.stroke({ color: opts.accentEdge, width: 2 });
  }

  // 7. Rivets
  if (opts.rivets) {
    drawRivetCorners(g, x, y, w, h, border);
  }
}

// ─── Inset rect ───────────────────────────────────────────────

/**
 * Draws a recessed/inset rectangle — darker at top, slightly lighter at
 * bottom — giving the impression of a surface cut into the panel.
 * Used for equipment slots, bar tracks, minimap screen areas.
 */
export function drawInsetRect(
  g: Graphics,
  x: number, y: number, w: number, h: number,
  r: number,
  fill: number,
  border: number,
  borderAlpha = 1,
): void {
  // Recessed gradient (inverted from raised panel)
  g.roundRect(x, y, w, h, r);
  g.fill(fill);

  // Top inset shadow
  g.moveTo(x + r,     y + 0.5);
  g.lineTo(x + w - r, y + 0.5);
  g.stroke({ color: darken(fill, 0.25), width: 1, alpha: 0.7 });

  // Bottom inset highlight
  g.moveTo(x + r,     y + h - 0.5);
  g.lineTo(x + w - r, y + h - 0.5);
  g.stroke({ color: lighten(fill, 0.12), width: 1, alpha: 0.5 });

  // Border
  g.roundRect(x, y, w, h, r);
  g.stroke({ color: border, width: 1, alpha: borderAlpha });
}

// ─── 3D button ────────────────────────────────────────────────

export interface ButtonOptions {
  /** Corner radius.  Default 6 */
  radius?: number;
  /** Key-travel depth in px.  Default 3 */
  depth?: number;
  /** If true, face is shifted down and depth block is hidden. */
  pressed?: boolean;
}

/**
 * Draws a 3D button onto `g` with origin (0, 0).
 * Caller is responsible for positioning the container and adding a Text child.
 *
 * Returns `faceY` so the caller can vertically centre text on the face.
 */
export function draw3DButton(
  g: Graphics,
  w: number, h: number,
  faceColor: number,
  opts: ButtonOptions = {},
): { faceY: number } {
  const r       = opts.radius  ?? 6;
  const depth   = opts.depth   ?? 3;
  const pressed = opts.pressed ?? false;
  const faceY   = pressed ? depth : 0;

  // Depth block (hidden when pressed)
  if (!pressed) {
    g.roundRect(0, depth, w, h, r);
    g.fill(darken(faceColor, 0.4));
  }

  // Face gradient
  g.roundRect(0, faceY, w, h, r);
  g.fill(faceColor);

  // Top highlight
  g.moveTo(r,     faceY + 0.5);
  g.lineTo(w - r, faceY + 0.5);
  g.stroke({ color: lighten(faceColor, 0.4), width: 1, alpha: 0.6 });

  // Bottom shadow
  g.moveTo(r,     faceY + h - 0.5);
  g.lineTo(w - r, faceY + h - 0.5);
  g.stroke({ color: darken(faceColor, 0.3), width: 1, alpha: 0.5 });

  return { faceY };
}

// ─── Rivet corners ────────────────────────────────────────────

/**
 * Draws four small bolt circles inset from each corner of a rectangle.
 * Gives panels a "bolted chassis" look.
 */
export function drawRivetCorners(
  g: Graphics,
  x: number, y: number, w: number, h: number,
  color: number,
  margin = 6,
): void {
  const rivetR = 2.5;
  const face   = darken(color, 0.04);
  const rim    = lighten(color, 0.14);
  const shadow = darken(color, 0.12);

  const corners: [number, number][] = [
    [x + margin,     y + margin    ],
    [x + w - margin, y + margin    ],
    [x + margin,     y + h - margin],
    [x + w - margin, y + h - margin],
  ];

  for (const [cx, cy] of corners) {
    // Drop shadow below
    g.circle(cx, cy + 1, rivetR);
    g.fill({ color: shadow, alpha: 0.45 });
    // Rivet body
    g.circle(cx, cy, rivetR);
    g.fill(face);
    g.stroke({ color: rim, width: 0.8 });
    // Specular highlight dot
    g.circle(cx - 0.8, cy - 0.9, 0.9);
    g.fill({ color: lighten(color, 0.5), alpha: 0.55 });
  }
}

// ─── Section header band ─────────────────────────────────────

/**
 * Draws a horizontal gradient band behind a section header.
 * Fades from `accentColor` on the left to `bgColor` on the right,
 * giving headers visual weight without heavy decoration.
 *
 * @param h  Band height in px (default 20)
 */
export function drawHeaderBand(
  g: Graphics,
  x: number, y: number, w: number,
  accentColor: number,
  _bgColor: number,
  h = 20,
): void {
  g.roundRect(x, y, w, h, 3);
  g.fill(accentColor);
}

// ─── Close button ────────────────────────────────────────────

export interface CloseButtonOptions {
  /** Button face width. Default 30. */
  width?: number;
  /** Button face height. Default 24. */
  height?: number;
  /** If true, tint slightly on hover (0xcccccc). Default true. */
  hoverTint?: boolean;
  /** Label glyph shown on the face. Default '\u2715' (✕). */
  label?: string;
  /** Label font size in px. Default 16. */
  labelSize?: number;
}

/**
 * Builds a standard "✕" close button and adds it to `parent` at (x, y).
 * Shared by BuildingUI / ForgeUI / TraderUI — previously copy-pasted 3×.
 */
export function buildCloseButton(
  parent: Container,
  x: number,
  y: number,
  accentColor: number,
  faceColor: number,
  onClose: () => void,
  opts: CloseButtonOptions = {},
): Container {
  const w          = opts.width     ?? 30;
  const h          = opts.height    ?? 24;
  const hoverTint  = opts.hoverTint ?? true;
  const label      = opts.label     ?? '\u2715';
  const labelSize  = opts.labelSize ?? 16;

  const btn = new Container();
  const bg = new Graphics();
  draw3DButton(bg, w, h, faceColor, { radius: 3, depth: 2 });
  btn.addChild(bg);

  const lbl = new Text({ text: label, style: { fontFamily: 'Offside', fontSize: labelSize, fill: accentColor } });
  lbl.anchor.set(0.5);
  lbl.position.set(w / 2, h / 2);
  btn.addChild(lbl);

  btn.position.set(x, y);
  btn.eventMode = 'static';
  btn.cursor = 'pointer';
  if (hoverTint) {
    btn.on('pointerover', () => { bg.tint = 0xcccccc; });
    btn.on('pointerout',  () => { bg.tint = 0xffffff; });
  }
  btn.on('pointerdown', () => { onClose(); });
  parent.addChild(btn);
  return btn;
}

// ─── Panel title band ────────────────────────────────────────

/**
 * Draws the standard full-width header band + left-aligned title text used
 * by the red-accent overlays (BuildingUI, TraderUI). Forge uses a different
 * header scheme and intentionally doesn't call this.
 */
export function buildPanelTitleBand(
  parent: Container,
  panelW: number,
  title: string,
  accentColor: number,
  accentDarkColor: number,
  pad = 14,
): void {
  const band = new Graphics();
  drawHeaderBand(band, 0, 4, panelW, accentDarkColor, 0x0c0e14, 30);
  parent.addChild(band);

  const titleText = new Text({
    text: title,
    style: { fontFamily: 'Gugi', fontSize: 19, fill: accentColor, dropShadow: GUGI_SHADOW },
  });
  titleText.position.set(pad, 8);
  parent.addChild(titleText);
}

// ─── Overlay result ──────────────────────────────────────────

/**
 * Canonical return shape for overlay builders (trader, forge, building).
 * `update` is called once per frame when the overlay is active; omit if
 * the overlay has no per-frame work.
 */
export interface OverlayResult {
  container: Container;
  update?: (dt: number) => void;
  /**
   * Optional teardown hook called before `container.destroy()`. Required for
   * overlays that attach global-pointer listeners (via `attachScrollDrag`),
   * since Pixi's `destroy({ children: true })` uses `removeAllListeners`
   * which does not decrement the EventSystem's `globalpointermove` refcount.
   */
  dispose?: () => void;
}

// ─── Status bar ───────────────────────────────────────────────

/**
 * Draws a complete status bar: inset track + cylindrical gradient fill +
 * leading-edge highlight.
 *
 * @param progress  Fill fraction 0–1
 */
export function drawStatusBar(
  g: Graphics,
  x: number, y: number, w: number, h: number,
  r: number,
  trackColor: number,
  fillColor: number,
  progress: number,
  borderColor: number,
  borderAlpha = 0.35,
): void {
  // Track — inset look
  g.roundRect(x, y, w, h, r);
  g.fill(trackColor);
  g.roundRect(x, y, w, h, r);
  g.stroke({ color: borderColor, width: 1, alpha: borderAlpha });
  // Top inset shadow
  g.moveTo(x + r,     y + 0.5);
  g.lineTo(x + w - r, y + 0.5);
  g.stroke({ color: darken(trackColor, 0.3), width: 1, alpha: 0.6 });

  if (progress <= 0) return;

  const fillW = Math.max(r * 2, w * progress);

  // Fill — cylindrical tube gradient (bright centre band)
  g.roundRect(x, y, fillW, h, r);
  g.fill(fillColor);

  // Leading-edge highlight
  if (progress < 0.98) {
    const edgeX = x + fillW;
    g.moveTo(edgeX, y + 1);
    g.lineTo(edgeX, y + h - 1);
    g.stroke({ color: lighten(fillColor, 0.55), width: 2, alpha: 0.45 });
  }
}
